import {
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { memo, useState, type ReactNode } from 'react';

export interface NavGroupProps {
  readonly label: string;
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** Drawer mini-variant — render children directly (icon-only), no group chrome. */
  readonly collapsed?: boolean;
  readonly defaultExpanded?: boolean;
  /** localStorage key suffix — remembers the expand/collapse choice across sessions. */
  readonly persistKey?: string;
  /**
   * Aggregate count bubbled onto the group header ONLY while the group is
   * collapsed, so a nested badge (e.g. the Leads new-count) isn't lost when
   * the group is closed.
   */
  readonly badgeContent?: number;
  /** aria-label for the bubbled badge. */
  readonly badgeLabel?: string;
}

const readPersisted = (key: string | undefined, fallback: boolean): boolean => {
  if (!key) return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === 'true';
  } catch {
    return fallback;
  }
};

/**
 * Collapsible sidebar group — a labeled toggle header over a nested `List`
 * of `NavItem`s (e.g. "Marketing" wrapping Campaigns + Leads inside the
 * Records section). The expand/collapse choice is persisted per
 * `persistKey` so it survives a reload.
 *
 * In the drawer's mini-variant (`collapsed`, the 64px icon rail) the group
 * chrome is dropped entirely and children render directly, icon-only, each
 * keeping its own badge — matching how a plain `NavItem` degrades in that
 * mode. There's no sensible "expand a nested group" affordance in a rail
 * that has no room for labels at all.
 *
 * When the group is collapsed (closed, drawer still expanded), `badgeContent`
 * bubbles onto the header as a trailing pill — mirroring `NavItem`'s own
 * expanded-mode badge treatment — so a hidden nested badge (Leads' unread
 * count) stays visible even when its parent group is closed.
 */
const NavGroupComponent = ({
  label,
  icon,
  children,
  collapsed = false,
  defaultExpanded = true,
  persistKey,
  badgeContent = 0,
  badgeLabel,
}: NavGroupProps): JSX.Element => {
  const storageKey = persistKey ? `qfly.nav.${persistKey}.expanded` : undefined;
  const [expanded, setExpanded] = useState(() => readPersisted(storageKey, defaultExpanded));

  // Drawer mini-variant: children are icon-only NavItems that keep their own
  // badges — render them directly, without the (label-bearing) group header.
  if (collapsed) {
    return <>{children}</>;
  }

  const toggle = (): void => {
    setExpanded((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, String(next));
        } catch {
          /* localStorage unavailable (private mode / quota) — ignore. */
        }
      }
      return next;
    });
  };

  const showBadge = !expanded && badgeContent > 0;
  const badgeAriaLabel = badgeLabel ?? `${badgeContent} new`;
  const badgeDisplay = badgeContent > 99 ? '99+' : badgeContent;

  const listId = `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <ListItemButton
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={listId}
        sx={{ minHeight: 32, borderRadius: 1, px: 1.25, py: 0.875, color: 'text.secondary' }}
      >
        {icon ? (
          <ListItemIcon sx={{ minWidth: 36, mr: 1.125, justifyContent: 'center', color: 'inherit' }}>
            {icon}
          </ListItemIcon>
        ) : null}
        <ListItemText primary={label} />
        {showBadge ? (
          <Chip
            label={badgeDisplay}
            color="error"
            size="small"
            aria-label={badgeAriaLabel}
            sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
          />
        ) : null}
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding id={listId} sx={{ pl: 1.5 }}>
          {children}
        </List>
      </Collapse>
    </>
  );
};

export const NavGroup = memo(NavGroupComponent);
